import { useOrganisation } from '@/app/redux/hooks/organisation';
import { isLimitedPlan } from '@/common/utils/license';
import { WarningIcon } from '@phosphor-icons/react';
import { Button } from '@shadcn/components/button';
import { Checkbox } from '@shadcn/components/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@shadcn/components/dialog';
import { Label } from '@shadcn/components/label';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@shadcn/components/tooltip';
import { Info } from 'lucide-react';
import { useState } from 'react';

interface ConfirmBulkAddModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (exportLoginCards: boolean) => void;
  newLicenses: number;
  licensesRemaining: number;
}

export default function ConfirmBulkAddModal({
  open,
  onClose,
  onConfirm,
  newLicenses,
  licensesRemaining,
}: ConfirmBulkAddModalProps) {
  const [exportLoginCards, setExportLoginCards] = useState(false);

  const plan = useOrganisation().organisation?.license.plan;

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Confirm Bulk Add</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col space-y-3">
          <p>
            Are you sure you want to add {newLicenses} learner
            {newLicenses === 1 ? '' : 's'}?
          </p>

          {isLimitedPlan(plan) &&
            (licensesRemaining - newLicenses >= 0 ? (
              <p className="mt-2 text-sm">
                You will have {licensesRemaining - newLicenses} license
                {licensesRemaining === 1 ? '' : 's'} remaining after this
                action.
              </p>
            ) : (
              <p className="mt-2 text-sm flex items-center gap-2 text-warning">
                <WarningIcon size={18} weight="bold" />
                This will put you over your seat limit by{' '}
                {Math.abs(licensesRemaining - newLicenses)} learner
                {licensesRemaining - newLicenses === -1 ? '' : 's'}.
              </p>
            ))}

          <Label className="flex items-center gap-2">
            <Checkbox
              checked={exportLoginCards}
              onCheckedChange={() => setExportLoginCards(!exportLoginCards)}
            />
            <span>Export login details</span>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info
                  size={16}
                  color="hsl(var(--primary))"
                  className="cursor-pointer"
                />
              </TooltipTrigger>
              <TooltipContent>
                Save a PDF with printable login cards on confirm
              </TooltipContent>
            </Tooltip>
          </Label>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => onConfirm(exportLoginCards)}>Confirm</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
